import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { InlineWidget } from "react-calendly";
import {
  FaCheckCircle,
  FaApple,
  FaEnvelope,
  FaCalendarAlt,
  FaRegClock,
  FaFileInvoiceDollar,
  FaMobileAlt,
  FaLaptopCode,
  FaBrain,
  FaSyncAlt,
  FaRocket,
  FaShieldAlt,
} from "react-icons/fa";
import { getCalendlyUtm, initAttribution } from "../lib/attribution";
import { initPixels, track } from "../lib/tracking";
import logoGymly from "../assets/images/logos/gymly.png";
import logoArauco from "../assets/images/logos/arauco.png";
import logoRetailTrust from "../assets/images/logos/retailtrust.png";

/* ─── animations ─── */

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── layout ─── */

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #07090f 0%, #0a0c1e 60%, #07090f 100%);
  color: #fff;
  font-family: "Roboto", sans-serif;
  /* espacio para que la barra fija de mobile no tape el último bloque */
  padding-bottom: 0;

  @media (max-width: 768px) {
    padding-bottom: 88px;
  }
`;

/* ─── hero (compacto: el calendario tiene que entrar casi en el primer scroll) ─── */

const HeroSection = styled.section`
  max-width: 820px;
  margin: 0 auto;
  padding: 56px 24px 28px;
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out both;

  @media (max-width: 768px) {
    padding: 32px 20px 20px;
  }
`;

const EyebrowTag = styled.span`
  display: inline-block;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.25);
  color: #00c8ff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  padding: 7px 20px;
  border-radius: 50px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 10px;
    letter-spacing: 1.8px;
    padding: 6px 16px;
    margin-bottom: 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.9em;
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -1px;
  background: linear-gradient(135deg, #ffffff 0%, #00c8ff 50%, #005bea 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.8em;
    margin-bottom: 12px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.08em;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.65;
  max-width: 580px;
  margin: 0 auto 24px;

  @media (max-width: 768px) {
    font-size: 0.97em;
    margin-bottom: 20px;
  }
`;

/* ─── benefits strip ─── */

const BenefitsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 8px;
  }
`;

const Benefit = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 200, 255, 0.06);
  border: 1px solid rgba(0, 200, 255, 0.18);
  padding: 8px 16px;
  border-radius: 50px;

  svg {
    color: #00c8ff;
    font-size: 13px;
    flex-shrink: 0;
  }

  span {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 0.3px;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    padding: 6px 12px;
    gap: 6px;
    svg { font-size: 11px; }
    span { font-size: 11px; }
  }
`;

/* ─── calendly embed ─── */

const CalendlySection = styled.section`
  max-width: 940px;
  margin: 0 auto;
  padding: 0 24px 56px;
  scroll-margin-top: 16px;
  animation: ${fadeInUp} 0.6s ease-out 0.15s both;

  @media (max-width: 768px) {
    padding: 0 0 40px;
  }
`;

const CalendlyLabel = styled.p`
  text-align: center;
  font-size: 0.8em;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 14px;

  @media (max-width: 768px) {
    padding: 0 20px;
    letter-spacing: 1.4px;
  }
`;

const CalendlyWrapper = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.02);

  @media (max-width: 768px) {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

/*
  Placeholder mientras el iframe de Calendly carga, para que no se vea un hueco
  negro. Se apaga con el evento load del iframe y con un timeout de respaldo,
  NO con los postMessage de Calendly: si ese mensaje no llega, el cartel se
  queda pegado justo encima de los horarios y la página parece rota.
  Cuando termina usa visibility:hidden, así no puede tapar nada ni siquiera
  con opacidad 0.
*/
const CalendlyLoading = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  pointer-events: none;
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.9em;
  opacity: ${({ $done }) => ($done ? 0 : 1)};
  visibility: ${({ $done }) => ($done ? "hidden" : "visible")};
  transition: opacity 0.4s ease, visibility 0s linear 0.4s;

  svg {
    font-size: 26px;
    color: rgba(0, 200, 255, 0.5);
  }
`;

/* ─── en qué trabajamos ─── */

const AreasSection = styled.section`
  max-width: 820px;
  margin: 0 auto 48px;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 20px;
    margin-bottom: 40px;
  }
`;

const AreasTitle = styled.h2`
  text-align: center;
  font-size: 1em;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 24px;
`;

const AreasGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  /* Solo colapsamos a una columna en pantallas muy angostas (iPhone SE 1ª gen).
     A 375px y 390px, que es la mayoría del tráfico, dos columnas entran bien. */
  @media (max-width: 340px) {
    grid-template-columns: 1fr;
  }
`;

const AreaCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 16px 18px;

  svg {
    color: #00c8ff;
    font-size: 17px;
    flex-shrink: 0;
  }

  span {
    font-size: 0.9em;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.35;
  }

  @media (max-width: 768px) {
    padding: 14px;
    gap: 10px;
    svg { font-size: 15px; }
    span { font-size: 0.84em; }
  }
`;

/* ─── qué cubrimos ─── */

const ExpectSection = styled.div`
  max-width: 700px;
  margin: 0 auto 48px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 24px 20px;
    margin: 0 20px 40px;
  }
`;

const ExpectTitle = styled.h2`
  font-size: 1em;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 20px;
`;

const ExpectList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ExpectItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.97em;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.55;

  svg {
    color: #00c8ff;
    font-size: 14px;
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

/* ─── prueba social ─── */

const ProofSection = styled.section`
  max-width: 820px;
  margin: 0 auto;
  padding: 0 24px 56px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0 20px 44px;
  }
`;

const ProofLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  margin-bottom: 18px;
`;

const ProofLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  flex-wrap: wrap;
  margin-bottom: 28px;

  img {
    height: 30px;
    width: auto;
    object-fit: contain;
    filter: grayscale(100%) brightness(2);
    opacity: 0.6;
  }

  @media (max-width: 600px) {
    gap: 18px;
    img { height: 24px; }
  }
`;

const AppleRibbon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  padding: 10px 20px 10px 16px;
  border-radius: 50px;

  svg {
    font-size: 22px;
    color: #fff;
  }

  .ribbon-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
  }

  .ribbon-eyebrow {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.45);
  }

  .ribbon-title {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
  }
`;

/* ─── fallback de contacto ─── */

const FallbackRow = styled.p`
  text-align: center;
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.4);
  padding: 0 24px 56px;
  margin: 0;

  a {
    color: #00c8ff;
    text-decoration: none;
    font-weight: 600;
    white-space: nowrap;
  }

  a:hover {
    text-decoration: underline;
  }

  svg {
    margin-right: 6px;
    vertical-align: -1px;
  }
`;

/* ─── barra fija de mobile ─── */

const StickyBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: rgba(7, 9, 15, 0.94);
    backdrop-filter: blur(14px);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    transform: translateY(${({ $visible }) => ($visible ? "0" : "120%")});
    transition: transform 0.3s ease;
  }
`;

const StickyButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c8ff 0%, #005bea 100%);
  color: #fff;
  border: none;
  padding: 15px 24px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.4px;
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
`;

/* ─── metadatos de la página ─── */

/*
  El servidor ya sirve /call con su propio <title> y sus OG tags para el crawler
  de Meta. Esto cubre el otro caso: cuando el usuario llega a /call navegando
  dentro del SPA desde la home, donde el HTML ya viene renderizado con los datos
  de la portada.
*/
const PAGE_TITLE = "Agenda 30 minutos para revisar tu proyecto | Monaco IT Services";
const PAGE_DESCRIPTION =
  "Conversemos sobre tu app móvil, plataforma web, integración de IA o la modernización de un sistema que ya no da más. Alcance, plazos y presupuesto referencial en la misma llamada, sin costo.";

function useCallPageMeta() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    const setAttr = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (!el) return null;
      const previous = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => el.setAttribute(attr, previous);
    };

    const restorers = [
      setAttr('meta[name="description"]', "content", PAGE_DESCRIPTION),
      setAttr('meta[property="og:title"]', "content", PAGE_TITLE),
      setAttr('meta[property="og:description"]', "content", PAGE_DESCRIPTION),
      setAttr('meta[property="og:url"]', "content", "https://monaco-it-services.cl/call"),
      setAttr('link[rel="canonical"]', "href", "https://monaco-it-services.cl/call"),
    ].filter(Boolean);

    return () => {
      document.title = previousTitle;
      restorers.forEach((restore) => restore());
    };
  }, []);
}

/* ─── component ─── */

const CALENDLY_URL =
  process.env.REACT_APP_CALENDLY_URL ||
  'https://calendly.com/mseguel-calendly-monaco-it-services/monaco-it-primera-reunion';

export const CallPage = () => {
  const calendarRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [showSticky, setShowSticky] = useState(false);

  useCallPageMeta();

  // Capturamos la atribución de la URL actual antes de leerla, para que tanto un
  // clic directo del anuncio (/call?utm_*) como la navegación interna del SPA
  // dejen los UTM en el embed de Calendly.
  const utm = useMemo(() => {
    initAttribution();
    return getCalendlyUtm();
  }, []);

  // Alto del iframe: en mobile Calendly necesita bastante más espacio, si no
  // aparece un scroll interno dentro del iframe que mata la conversión.
  const [embedHeight, setEmbedHeight] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 768 ? 1080 : 720
  );

  useEffect(() => {
    const onResize = () => setEmbedHeight(window.innerWidth <= 768 ? 1080 : 720);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Pixel + PageView. initPixels es idempotente, así que no duplica nada si el
  // usuario llegó primero a la home.
  useEffect(() => {
    initPixels();
  }, []);

  /*
    Apagado del placeholder. Depende del evento load del iframe, que siempre
    dispara, más un timeout de respaldo por si el load se pierde (bfcache,
    iframe reemplazado en un re-render). Antes esto colgaba del postMessage
    calendly.event_type_viewed y si ese mensaje no llegaba, el cartel
    "Cargando horarios" se quedaba fijo encima del calendario.
  */
  useEffect(() => {
    const wrapper = calendarWrapperRef.current;
    if (!wrapper) return undefined;

    let cleanupIframe = null;
    const markLoaded = () => setCalendarLoaded(true);

    // react-calendly monta el iframe en su propio ciclo, así que lo buscamos
    // por un rato corto en vez de asumir que ya está en el primer render.
    const poll = setInterval(() => {
      const iframe = wrapper.querySelector("iframe");
      if (!iframe) return;
      clearInterval(poll);
      iframe.addEventListener("load", markLoaded);
      cleanupIframe = () => iframe.removeEventListener("load", markLoaded);
    }, 200);

    const fallback = setTimeout(markLoaded, 8000);

    return () => {
      clearInterval(poll);
      clearTimeout(fallback);
      if (cleanupIframe) cleanupIframe();
    };
  }, []);

  // La barra fija solo aparece cuando el calendario quedó fuera de pantalla.
  useEffect(() => {
    const node = calendarRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setShowSticky(!entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const scrollToCalendar = useCallback(() => {
    calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Embudo de eventos de Meta a partir de los mensajes del iframe de Calendly:
  //   ViewContent -> el calendario realmente cargó
  //   Lead        -> eligió día y hora (intención alta, buen evento de respaldo)
  //   Schedule    -> reserva confirmada (la conversión real)
  useEffect(() => {
    function handleMessage(e) {
      if (e.origin !== "https://calendly.com") return;
      const event = e.data && e.data.event;
      if (!event || typeof event !== "string" || !event.startsWith("calendly.")) return;

      // Nota: el apagado del placeholder NO cuelga de acá a propósito, va por el
      // evento load del iframe. Esto es solo tracking.
      if (event === "calendly.event_type_viewed") {
        track("ViewContent", { content_name: "call_page_calendar" });
        return;
      }

      if (event === "calendly.date_and_time_selected") {
        track("Lead", { content_name: "call_page_slot_selected" });
        return;
      }

      if (event === "calendly.event_scheduled") {
        const inviteeUri = e.data?.payload?.invitee?.uri;
        // Mismo event_id que va a usar el webhook del servidor, derivado del
        // UUID del invitee, para que Meta deduplique las dos copias.
        const inviteeUuid = String(inviteeUri || "").split("/").pop();
        track(
          "Schedule",
          { content_name: "diagnostico_30min" },
          {
            eventId: inviteeUuid ? `cal_${inviteeUuid}` : undefined,
            calendlyInviteeUri: inviteeUri,
            liConversionId: process.env.REACT_APP_LI_BOOKING_CONVERSION_ID,
          }
        );

        /*
          Mandamos a /gracias-agenda para que Meta pueda optimizar por "llegó a
          esta URL", que es la regla que necesita mientras el evento Schedule
          siga bloqueado por falta de volumen.

          Va sobre la ventana padre a propósito. Si se configura el redirect
          nativo de Calendly, con un embed inline la redirección ocurre DENTRO
          del iframe: el usuario no navega de verdad y el pixel dispararía desde
          un marco anidado. Navegando el padre queda una carga real de página
          con su propio PageView, que es lo que la regla de URL necesita.

          Dejamos el URI del invitee en sessionStorage para que la página de
          gracias pueda pedirle al servidor los datos hasheados del invitado. No
          va por query string: es un puntero a datos personales y no tiene por
          qué quedar en la URL, ni en logs, ni en el referer.
        */
        try {
          sessionStorage.setItem(
            "monaco_last_booking",
            JSON.stringify({ inviteeUri: inviteeUri || null, ts: Date.now() })
          );
        } catch (err) {
          /* modo privado sin storage: la página igual funciona, con menos señal */
        }

        // Margen corto para que alcancen a salir el beacon del pixel y el POST
        // a nuestro servidor antes de cambiar de página.
        window.setTimeout(function () {
          window.location.assign("/gracias-agenda");
        }, 700);
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <PageWrapper>
      <HeroSection>
        <EyebrowTag>Primera reunión gratuita · 30 min</EyebrowTag>
        <HeroTitle>Agenda 30 minutos para revisar tu proyecto</HeroTitle>
        <HeroSubtitle>
          Apps móviles, plataformas web, integración de IA o modernizar un sistema
          que ya no da más. Te decimos qué se puede construir, en cuánto tiempo y
          con qué presupuesto.
        </HeroSubtitle>

        <BenefitsRow>
          <Benefit>
            <FaRegClock />
            <span>Alcance y plazos concretos</span>
          </Benefit>
          <Benefit>
            <FaFileInvoiceDollar />
            <span>Presupuesto referencial</span>
          </Benefit>
          <Benefit>
            <FaCheckCircle />
            <span>Sin compromiso</span>
          </Benefit>
        </BenefitsRow>
      </HeroSection>

      <CalendlySection ref={calendarRef}>
        <CalendlyLabel>Elige el horario que mejor te acomode</CalendlyLabel>
        <CalendlyWrapper ref={calendarWrapperRef} style={{ minHeight: embedHeight }}>
          <CalendlyLoading $done={calendarLoaded}>
            <FaCalendarAlt />
            <span>Cargando horarios disponibles...</span>
          </CalendlyLoading>
          <InlineWidget
            url={CALENDLY_URL}
            utm={utm}
            pageSettings={{
              hideEventTypeDetails: true,
              hideGdprBanner: true,
              backgroundColor: '07090f',
              textColor: 'ffffff',
              primaryColor: '00c8ff',
            }}
            styles={{ minWidth: '320px', height: `${embedHeight}px`, position: 'relative' }}
          />
        </CalendlyWrapper>
      </CalendlySection>

      <ProofSection>
        <ProofLabel>Empresas que confían en nosotros</ProofLabel>
        <ProofLogos>
          <img src={logoArauco} alt="Arauco" />
          <img src={logoGymly} alt="Gymly" />
          <img src={logoRetailTrust} alt="Retail Trust" />
        </ProofLogos>
        <AppleRibbon>
          <FaApple />
          <div className="ribbon-text">
            <span className="ribbon-eyebrow">Apple WWDC · Cupertino 2024</span>
            <span className="ribbon-title">
              Primer chileno en ganar el Swift Student Challenge
            </span>
          </div>
        </AppleRibbon>
      </ProofSection>

      <AreasSection>
        <AreasTitle>En qué te podemos ayudar</AreasTitle>
        <AreasGrid>
          <AreaCard>
            <FaMobileAlt />
            <span>Apps móviles iOS y Android</span>
          </AreaCard>
          <AreaCard>
            <FaLaptopCode />
            <span>Plataformas web y SaaS</span>
          </AreaCard>
          <AreaCard>
            <FaSyncAlt />
            <span>Modernizar sistemas legacy</span>
          </AreaCard>
          <AreaCard>
            <FaBrain />
            <span>Integración de IA</span>
          </AreaCard>
          <AreaCard>
            <FaRocket />
            <span>MVP en 8 a 16 semanas</span>
          </AreaCard>
          <AreaCard>
            <FaShieldAlt />
            <span>Ciberseguridad y pentesting</span>
          </AreaCard>
        </AreasGrid>
      </AreasSection>

      <ExpectSection>
        <ExpectTitle>Qué cubrimos en la reunión</ExpectTitle>
        <ExpectList>
          <ExpectItem>
            <FaCheckCircle />
            <span>
              Entender qué necesitas construir y en qué contexto: producto nuevo,
              integración con lo que ya tienes o rescate de un sistema heredado.
            </span>
          </ExpectItem>
          <ExpectItem>
            <FaCheckCircle />
            <span>
              Definir el alcance real: qué entra en una primera versión que sirva y
              qué conviene dejar para después.
            </span>
          </ExpectItem>
          <ExpectItem>
            <FaCheckCircle />
            <span>
              Plazos y presupuesto referencial en la misma llamada, no en dos
              semanas.
            </span>
          </ExpectItem>
          <ExpectItem>
            <FaCheckCircle />
            <span>
              Si no somos el partner correcto para tu caso, te lo decimos derecho y
              te orientamos igual.
            </span>
          </ExpectItem>
        </ExpectList>
      </ExpectSection>

      <FallbackRow>
        <FaEnvelope />
        ¿No encuentras un horario que te sirva? Escríbenos a{" "}
        <a href="mailto:mseguel@monaco-it-services.cl">
          mseguel@monaco-it-services.cl
        </a>
      </FallbackRow>

      <StickyBar $visible={showSticky}>
        <StickyButton onClick={scrollToCalendar}>
          <FaCalendarAlt /> Ver horarios disponibles
        </StickyButton>
      </StickyBar>
    </PageWrapper>
  );
};
